import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useCallsByPhone } from '../../api/telemetry'
import { useTelemetryStore } from '../../stores/telemetryStore'
import { StatusBadge } from '../shared/StatusBadge'
import { DirectionBadge } from '../shared/DirectionBadge'
import { Pagination } from '../shared/Pagination'
import { SpinLoader } from '../shared/SpinLoader'
import { ErrorCard } from '../shared/ErrorCard'
import { formatDateTime, formatDuration } from '../../lib/utils'

const LIMIT = 10

export function PhoneCallHistory({ phoneNumber }: { phoneNumber: string }) {
  const { t } = useTranslation('calls')
  const [offset, setOffset] = useState(0)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useCallsByPhone(phoneNumber, { limit: LIMIT, offset })
  const recentEvents = useTelemetryStore((s) => s.recentEvents)

  // Refresh call history when a call ends
  useEffect(() => {
    const endedEvent = recentEvents.find((e) => e.event_type === 'call.ended')
    if (endedEvent) {
      queryClient.invalidateQueries({ queryKey: ['callsByPhone', phoneNumber] })
    }
  }, [recentEvents, phoneNumber, queryClient])

  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <h2 className="mb-3 text-lg font-semibold">{t('history.title')}</h2>

      {isLoading && <SpinLoader size="sm" label={t('history.loading')} />}

      {isError && <ErrorCard message={t('history.loadFailed')} onRetry={() => refetch()} />}

      {data && data.calls.length === 0 && (
        <p className="py-4 text-center text-sm text-text-muted">{t('history.emptyForNumber')}</p>
      )}

      {data && data.calls.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="pb-2 pr-4">{t('history.table.time')}</th>
                  <th className="pb-2 pr-4">{t('history.table.direction')}</th>
                  <th className="pb-2 pr-4">{t('history.table.status')}</th>
                  <th className="pb-2 pr-4">{t('history.table.duration')}</th>
                  <th className="pb-2">{t('history.table.endedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {data.calls.map((call) => (
                  <tr
                    key={call.call_sid}
                    onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                    className="cursor-pointer border-b border-border/50 hover:bg-surface-elevated/50 transition-colors"
                  >
                    <td className="py-2 pr-4 text-text-secondary">
                      {formatDateTime(call.started_at)}
                    </td>
                    <td className="py-2 pr-4">
                      <DirectionBadge direction={call.direction} />
                    </td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={call.status} />
                    </td>
                    <td className="py-2 pr-4 font-mono text-text-secondary">
                      {formatDuration(call.duration_s)}
                    </td>
                    <td className="py-2 text-text-muted">{call.ended_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card layout */}
          <div className="space-y-2 md:hidden">
            {data.calls.map((call) => (
              <div
                key={call.call_sid}
                onClick={() => navigate(`/app/calls/${call.call_sid}`)}
                className="cursor-pointer rounded-lg border border-border/50 bg-surface p-3 transition-colors hover:bg-surface-elevated/50"
              >
                <div className="flex items-center justify-between">
                  <DirectionBadge direction={call.direction} />
                  <StatusBadge status={call.status} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-text-secondary">
                  <span className="font-mono">{formatDuration(call.duration_s)}</span>
                  <span>{formatDateTime(call.started_at)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <Pagination
              offset={offset}
              limit={LIMIT}
              total={data.total}
              onPageChange={setOffset}
            />
          </div>
        </>
      )}
    </div>
  )
}
